import { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarDays, Clock, Search, Stethoscope, User } from 'lucide-react';
import toast from 'react-hot-toast';
import { listAppointments } from '../api/appointments';
import { listPetMedicalRecords } from '../api/pets';
import { listProfessionals } from '../api/professionals';
import { getErrorMessage } from '../api/response';
import AtencionModal from '../components/AtencionModal';
import Button from '../components/Button';
import FormField, { inputClassName } from '../components/FormField';
import PageHeader from '../components/PageHeader';
import { EmptyState, LoadingState } from '../components/PageState';
import StatusBadge from '../components/StatusBadge';
import { dateTime } from '../utils/format';
import { appointmentStatus, getStatusConfig } from '../utils/status';

const todayIso = () => new Date().toISOString().slice(0, 10);

const isActiveVeterinarian = (professional) => {
  return professional.role === 'veterinarian'
    && Number(professional.is_active ?? professional.active ?? 1) !== 0;
};

const canAttend = (appointment) => !['completed', 'cancelled', 'no_show'].includes(appointment.status);

const Atencion = () => {
  const [professionals, setProfessionals] = useState([]);
  const [professionalId, setProfessionalId] = useState('');
  const [date, setDate] = useState(todayIso());
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);

  const veterinarians = useMemo(() => professionals.filter(isActiveVeterinarian), [professionals]);
  const selectedProfessional = useMemo(() => (
    professionals.find((professional) => String(professional.id) === String(professionalId))
  ), [professionalId, professionals]);

  const loadProfessionals = useCallback(async () => {
    try {
      setLoadingProfessionals(true);
      const data = await listProfessionals({ include_inactive: 1 });
      setProfessionals(data);
      const firstVeterinarian = data.find(isActiveVeterinarian);
      setProfessionalId((current) => current || (firstVeterinarian ? String(firstVeterinarian.id) : ''));
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar los veterinarios'));
    } finally {
      setLoadingProfessionals(false);
    }
  }, []);

  const loadAppointments = useCallback(async () => {
    if (!professionalId) {
      setAppointments([]);
      return;
    }

    try {
      setLoadingAppointments(true);
      const from = `${date} 00:00:00`;
      const to = `${date} 23:59:59`;
      const data = await listAppointments({
        from,
        to,
        professional_id: professionalId,
        limit: 100
      });
      const sorted = [...data].sort((a, b) => String(a.starts_at).localeCompare(String(b.starts_at)));
      setAppointments(sorted);
      setSelectedAppointment((current) => (
        current && !sorted.some((appointment) => appointment.id === current.id) ? null : current
      ));
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar las citas'));
    } finally {
      setLoadingAppointments(false);
    }
  }, [date, professionalId]);

  const loadRecords = useCallback(async (petId) => {
    if (!petId) {
      setSelectedRecords([]);
      return;
    }

    try {
      setLoadingRecords(true);
      setSelectedRecords(await listPetMedicalRecords(petId));
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo cargar la ficha clínica'));
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    loadProfessionals();
  }, [loadProfessionals]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    if (selectedAppointment?.pet_id) {
      loadRecords(selectedAppointment.pet_id);
    } else {
      setSelectedRecords([]);
    }
  }, [loadRecords, selectedAppointment]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Atención"
        description="Selecciona un veterinario y fecha para abrir la ficha clínica completa de la atención."
      />

      <section className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto] md:items-end">
          <FormField label="Veterinario">
            <select
              className={inputClassName}
              value={professionalId}
              onChange={(event) => {
                setProfessionalId(event.target.value);
                setSelectedAppointment(null);
              }}
              disabled={loadingProfessionals}
            >
              <option value="">Selecciona veterinario</option>
              {veterinarians.map((professional) => (
                <option key={professional.id} value={professional.id}>{professional.full_name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Fecha">
            <input
              type="date"
              className={inputClassName}
              value={date}
              onChange={(event) => {
                setDate(event.target.value);
                setSelectedAppointment(null);
              }}
            />
          </FormField>
          <Button type="button" icon={Search} variant="secondary" onClick={loadAppointments} className="w-full">
            Actualizar
          </Button>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-950">Citas para atender</h2>
            <p className="text-sm text-gray-500">
              {selectedProfessional ? selectedProfessional.full_name : 'Selecciona un veterinario'} · {date}
            </p>
          </div>
          <CalendarDays className="h-5 w-5 text-gray-400" />
        </div>

        {loadingAppointments ? (
          <LoadingState rows={8} />
        ) : appointments.length ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {appointments.map((appointment) => {
              const status = getStatusConfig(appointmentStatus, appointment.status);
              const canAtt = canAttend(appointment);
              
              return (
                <div
                  key={appointment.id}
                  className={`rounded-lg border p-4 transition ${
                    canAtt
                      ? 'border-gray-200 hover:border-blue-400 hover:shadow-md cursor-pointer bg-white'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-950 text-sm truncate">
                        {appointment.pet_name || `Mascota #${appointment.pet_id}`}
                      </h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                        <User className="h-3 w-3" />
                        {appointment.customer_name || `Cliente #${appointment.customer_id}`}
                      </p>
                    </div>
                    <StatusBadge label={status.label} color={status.color} />
                  </div>

                  <div className="space-y-2 mb-4 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="h-3 w-3 text-gray-400" />
                      <span>{dateTime(appointment.starts_at)}</span>
                    </div>
                    {appointment.service_name && (
                      <div className="flex items-center gap-2">
                        <Stethoscope className="h-3 w-3 text-gray-400" />
                        <span className="truncate">{appointment.service_name}</span>
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      setSelectedAppointment(appointment);
                      loadRecords(appointment.pet_id);
                    }}
                    disabled={!canAtt}
                  >
                    Atender
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            title="Sin citas"
            message={professionalId ? 'No hay citas para este veterinario en la fecha seleccionada' : 'Selecciona un veterinario para ver sus citas'}
          />
        )}
      </section>

      <AtencionModal
        appointment={selectedAppointment}
        records={selectedRecords}
        loadingRecords={loadingRecords}
        professionalId={professionalId}
        professional={selectedProfessional}
        onClose={() => setSelectedAppointment(null)}
        onSuccess={loadAppointments}
      />
    </div>
  );
};

export default Atencion;
